<template>
  <div class="page">
    <BackLink
      :route-from="currentUsergroup ? {
        name: currentUsergroup.organisation ? 'OrganisationDetail' : 'GroupDetail',
        params: {
          id: currentUsergroup.organisation ? currentUsergroup.organisation.id : currentUsergroup.id
        }
      } : null"
      route-name="Accounts"
      :title="currentUsergroup ? currentUsergroup.display_name : capitalizeFirstLetter($t('words.accountsManagement'))"
    />
    <div :class="currentIndexerLogs && currentIndexerLogs.length ? 'container-xxl' : 'container-xl'">
      <b-overlay
        no-wrap
        :show="loading"
        variant="light"
        opacity="0.5"
        rounded="false"
        style="z-index: 1040;"
      />
      <h2 v-if="currentIndexer && currentIndexer.name">
        {{ currentIndexer.name }}
      </h2>
      <ValidationObserver v-slot="{ handleSubmit }">
        <div class="row">
          <div :class="currentIndexerLogs && currentIndexerLogs.length ? 'col-9' : 'col'">
            <IndexerForm
              :data="initData"
              @set="setForm"
            />
            <div class="row mt-3">
              <div class="col indexer-form-footer">
                <button
                  type="button"
                  class="btn btn-warning btn-lg"
                  @click="testIndexer"
                >
                  {{ capitalizeFirstLetter($t('words.test')) }}
                </button>
                <button
                  type="button"
                  :class="{
                    disabled: !canUpdateIndexer
                  }"
                  class="btn btn-primary btn-lg"
                  @click="handleSubmit(updateIndexer)"
                >
                  {{ capitalizeFirstLetter($t('buttons.update')) }}
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="currentIndexerLogs && currentIndexerLogs.length"
            class="col"
          >
            <IndexerLogs />
          </div>
        </div>
        <div
          v-if="trialError && trialError.length"
          id="indexerTrialError"
          class="alert alert-danger alert-dismissible fade show my-4"
          role="alert"
        >
          <b>
            {{ $t('indexers.testError') }}
          </b>
          <br>
          {{ trialError[0] }}
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="alert"
            aria-label="Close"
            @click="SET_TRIAL_ERROR(null)"
          />
        </div>
      </ValidationObserver>
    </div>

    <b-modal
      v-model="isTrialModalOpen"
      size="xl"
      :title="$t('indexers.testResults')"
      scrollable
      :hide-footer="true"
      @close="SET_TRIAL_RESULTS(null)"
    >
      <div>
        <table class="table table-striped">
          <thead class="table-dark">
            <tr>
              <th scope="col">
                {{ $t('indexers.table.name') }}
              </th>
              <th scope="col">
                {{ $t('indexers.table.description') }}
              </th>
              <th scope="col">
                {{ $t('indexers.table.publicationDate') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="result in trialResults"
              :key="result.identifier"
            >
              <td>{{ result.title }}</td>
              <td>{{ result.abstract }}</td>
              <td class="align-middle">
                {{ new Date(result.publication_date).toLocaleString().slice(0, 10) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapMutations, mapActions } from 'vuex';

import BackLink from '@/components/Elements/BackLink.vue';
import IndexerForm from '@/app/indexer/components/IndexerForm.vue';
import IndexerLogs from '@/app/indexer/components/IndexerLogs.vue';

import { ValidationObserver } from 'vee-validate';

export default {
  name: 'IndexerDetail',

  components: {
    BackLink,
    IndexerForm,
    IndexerLogs,
    ValidationObserver
  },

  data() {
    return {
      loading: false,
      initData: null,
      formData: null,
      canUpdateIndexer: false,
      isTrialModalOpen: false
    };
  },

  computed: {
    ...mapState('usergroups', [
      'currentUsergroup'
    ]),
    ...mapState('indexers', [
      'indexersTypes',
      'currentIndexer',
      'currentIndexerLogs',
      'trialResults',
      'trialError'
    ]),
  },

  watch: {
    trialResults: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.isTrialModalOpen = newValue.length > 0;
        } else {
          this.isTrialModalOpen = false;
        }
      }
    }
  },

  async created() {
    try {
      this.loading = true;
      this.SET_TRIAL_ERROR(null);
      await this.GET_CURRENT_INDEXER(this.$route.params.indexerId);
      await this.GET_CURRENT_INDEXER_LOGS(this.$route.params.indexerId);
      this.initData = {
        name: this.currentIndexer.name,
        description: this.currentIndexer.description,
        url: this.currentIndexer.options.url,
        command: this.indexersTypes.find(el => el.value === this.currentIndexer.command),
        organisation:
          this.currentIndexer.options.organisation && this.currentIndexer.options.organisation.length ?
            this.currentIndexer.options.organisation[0] :
            null,
        forcedOrganisations: this.currentIndexer.options.force_organisation,
        pattern: this.currentIndexer.options.href_pattern,
        scheduler: this.currentIndexer.scheduler,
      };
      this.loading = false;
    } catch (err) {
      console.error(err);
      this.loading = false;
    }
  },

  mounted () {
    this.SET_TRIAL_ERROR(null);
    this.SET_TRIAL_RESULTS(null);
    // TODO: set scrollBehavior in router
    // TODO: (use meta field to change behavior between pages + use back-lnk as anchor)
    const pageTop = document.getElementsByClassName('back-link')[0];
    pageTop.scrollIntoView({ behavior: 'smooth' });
  },

  methods: {
    ...mapMutations('indexers', [
      'SET_TRIAL_ERROR',
      'SET_TRIAL_RESULTS'
    ]),
    ...mapActions('indexers', [
      'GET_CURRENT_INDEXER',
      'UPDATE_CURRENT_INDEXER',
      'TEST_CURRENT_INDEXER',
      'GET_CURRENT_INDEXER_LOGS'
    ]),

    setForm(e) {
      this.formData = e;
      this.canUpdateIndexer = !_.isEqual(this.initData, this.formData);
    },

    async testIndexer() {
      try {
        this.loading = true;
        await this.TEST_CURRENT_INDEXER(this.$route.params.indexerId);
        const testResultsContainerEl = document.getElementById('indexerTrialResults');
        const testErrorContainerEl = document.getElementById('indexerTrialError');
        if (testResultsContainerEl) {
          testResultsContainerEl
            .scrollIntoView({
              behavior: 'smooth'
            });
        } else if (testErrorContainerEl) {
          testErrorContainerEl
            .scrollIntoView({
              behavior: 'smooth'
            });
        }
        this.loading = false;
      } catch (err) {
        console.error(err);
        this.loading = false;
      }
    },

    async updateIndexer() {
      try {
        this.loading = true;
        const data = {
          name: this.formData.name,
          ...this.formData.description && {
            description: this.formData.description
          },
          options: {
            url: this.formData.url,
            href_pattern: this.formData.pattern,
            ...this.formData.organisation && {
              organisation: [this.formData.organisation]
            }
          },
          command: this.formData.command.value,
          scheduler: this.formData.scheduler,
          usergroup_id: this.currentIndexer.usergroup.id
        };
        await this.UPDATE_CURRENT_INDEXER({
          id: this.$route.params.indexerId,
          data: data
        });
        this.loading = false;
      } catch (err) {
        console.error(err);
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="less" scoped>
h2, h6 {
  color: @blue;
}
.indexer-form-footer {
  display: flex;
  justify-content: space-between;
}
#indexerTrialResults, #indexerTrialError {
  scroll-margin-top: 5rem;
}
</style>